<template>
  <div class="config-item">
    <label class="config-label">
      <v-icon x-small class="mr-1">{{ icon }}</v-icon>
      {{ label }}
    </label>
    
    <v-text-field
      v-if="type === 'number'"
      :value="value"
      type="number"
      :min="min"
      :max="max"
      :placeholder="placeholder"
      :suffix="suffix"
      solo
      flat
      dense
      hide-details="auto"
      :disabled="disabled"
      :rules="rules"
      class="config-input mt-2"
      @input="$emit('input', parseFloat($event))"
    >
      <template v-if="showControls" v-slot:append>
        <div class="input-controls">
          <v-btn
            icon
            x-small
            @click="decrement"
            :disabled="value <= min || disabled"
            class="control-btn"
          >
            <v-icon x-small>mdi-minus</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            @click="increment"
            :disabled="value >= max || disabled"
            class="control-btn"
          >
            <v-icon x-small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>
    </v-text-field>

    <v-select
      v-else-if="type === 'select'"
      :value="value"
      :items="items"
      item-text="text"
      item-value="value"
      solo
      flat
      dense
      hide-details
      :disabled="disabled"
      class="config-input mt-2"
      @input="$emit('input', $event)"
    >
      <template v-slot:selection="{ item }">
        <span class="text-body-2">{{ item.text }}</span>
      </template>
    </v-select>

    <p class="config-hint mt-1">
      {{ hint }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'ConfigurationField',
  
  props: {
    label: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: 'mdi-cog'
    },
    value: {
      type: [Number, Boolean, String],
      required: true
    },
    type: {
      type: String,
      default: 'number',
      validator: (value) => ['number', 'select'].includes(value)
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    placeholder: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    showControls: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    increment() {
      if (this.value < this.max) {
        this.$emit('input', this.value + 1);
      }
    },

    decrement() {
      if (this.value > this.min) {
        this.$emit('input', this.value - 1);
      }
    }
  }
};
</script>

<style scoped>
.config-item {
  position: relative;
}

.config-label {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #424242;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0;
}

.config-input >>> .v-input__control {
  min-height: 40px !important;
}

.config-input >>> .v-input__slot {
  background: #fafafa !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 6px !important;
  padding: 0 12px !important;
  min-height: 40px !important;
  box-shadow: none !important;
}

.config-input >>> .v-input__slot:hover {
  background: #f5f5f5 !important;
  border-color: #bdbdbd !important;
}

.config-input.v-input--is-focused >>> .v-input__slot {
  background: #ffffff !important;
  border-color: #424242 !important;
  box-shadow: 0 0 0 2px rgba(66, 66, 66, 0.1) !important;
}

.config-hint {
  font-size: 0.6875rem;
  color: #757575;
  margin: 4px 0 0 0;
}

/* Input controls */
.input-controls {
  display: flex;
  gap: 2px;
}

.control-btn {
  width: 24px !important;
  height: 24px !important;
  background: transparent !important;
}

.control-btn:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

/* Clean input appearance */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
